import Vue from 'vue';

import differenceWith from 'lodash/differenceWith';
import every from 'lodash/every';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import flatMap from 'lodash/flatMap';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNull from 'lodash/isNull';
import keys from 'lodash/keys';
import last from 'lodash/last';
import map from 'lodash/map';
import some from 'lodash/some';
import merge from 'lodash/merge';
import keyBy from 'lodash/keyBy';
import reduce from 'lodash/reduce';
import assign from 'lodash/assign';

import i18n from '@/plugins/i18n';
import AuthorisationUtil from '@/util/authorisation-util';
import DateTimeUtil from '@/util/date-time-util';
import WorkScheduleUtil from '@/util/work-schedule-util';
import constants from '@/constants';
import getTimePairClockingType from '@/mixins/timepairs/getTimePairClockingType';
import hasLocationFeature from '@/mixins/location/hasLocationFeature';
import includes from 'lodash/includes';

const mutations = {
  // General
  setPageTitle: (state, pageTitle) => {
    state.menuData.pageTitle = pageTitle;
  },

  setNotifications: (state, notifications) => {
    state.notifications = notifications;
  },

  updateNotificationCount: (state, count) => {
    state.notificationCount = count;
  },

  removeNotification: (state, notificationId) => {
    const index = findIndex(state.notifications, ['notificationId', notificationId]);
    state.notifications.splice(index, 1);
    state.notificationCount = state.notifications.length;
  },

  removeAllNotifications: (state) => {
    state.notifications = [];
    state.notificationCount = 0;
  },

  setMissedClockOutWarned: (state, warned) => {
    state.warnedOfMissedClockOut = warned;
  },

  setLicense(state, license) {
    state.license.name = license.account.name;

    state.license.expiry_date = find(
      license.products,
      ['code', constants.LICENSE_BASE_PRODUCT_CODE],
    ).licenses[0].expiryDate;
  },

  setVersion(state, version) {
    state.version = version;
  },

  setCorrectionReasons: (state, correctionReasons) => {
    state.correctionReasons = correctionReasons;
  },

  // Login
  login: (state, userProfile) => {
    state.employee = userProfile.employee;
    state.userConfiguration = userProfile.userConfiguration;
    state.notificationCount = userProfile.notificationCount;
    state.workSchedules = keyBy(userProfile.workSchedules, 'assignmentId');
    i18n.locale = userProfile.userConfiguration.locale === null ? i18n.locale
      : userProfile.userConfiguration.locale;

    if (userProfile.activeTimePair) {
      state.activeTimePair = userProfile.activeTimePair;
      const segmentTimePair = last(userProfile.activeTimePair.segmentTimePairs);
      if (segmentTimePair && segmentTimePair.clockOut == null) {
        state.activeSegment = segmentTimePair;
        state.activeSegmentType = segmentTimePair.pairType;
        state.timer.trueClockInTime = segmentTimePair.clockIn.date;
        state.timer.timeZoneOffset = segmentTimePair.clockIn.timeZoneOffset;
      } else {
        state.timer.trueClockInTime = userProfile.activeTimePair.clockIn.date;
        state.timer.timeZoneOffset = userProfile.activeTimePair.clockIn.timeZoneOffset;
      }
    }

    DateTimeUtil.switchTimeFormat(state.userConfiguration.is24HourDateTime);
  },

  setEmployeeTimePairsModel: (state, timePairData) => {
    timePairData = filter(timePairData, (timePair) => !timePair.deletedDate);

    forEach(timePairData, (timePair) => {
      timePair.segmentTimePairs = filter(
        timePair.segmentTimePairs,
        (segmentPair) => !segmentPair.deletedDate,
      );
    });

    state.timePairs = map(timePairData, (timePair) => {
      if (timePair.startingWorkScheduleDay !== null) {
        return WorkScheduleUtil.getTimePairWithWorkScheduleData(state, timePair);
      }
      return timePair;
    });
  },

  updateEmployeeTimePairs: (state, payload) => {
    payload.newTimePair.segmentTimePairs = filter(
      payload.newTimePair.segmentTimePairs,
      (pair) => !pair.deletedDate,
    );
    if (payload.timePairId === -1) {
      state.timePairs.push(WorkScheduleUtil.getTimePairWithWorkScheduleData(state, payload.newTimePair));
      state.timePairs.sort(
        (timePairA, timePairB) => DateTimeUtil.dateTimeIsGreater(timePairA.clockIn.date, timePairB.clockIn.date),
      );
    } else {
      let shiftTimePairIndex = findIndex(state.timePairs, { id: payload.timePairId });
      if (shiftTimePairIndex === -1) {
        shiftTimePairIndex = state.timePairs
        .findIndex((timePair) => timePair.adjustedVersion.id === payload.timePairId);
      }
      state.timePairs.splice(
        shiftTimePairIndex,
        1,
        WorkScheduleUtil.getTimePairWithWorkScheduleData(state, payload.newTimePair),
      );
    }
  },

  removeEmployeeTimePair: (state, payload) => {
    payload.newShiftTimePair.segmentTimePairs = filter(
      payload.newShiftTimePair.segmentTimePairs,
      (pair) => !pair.deletedDate,
    );

    const shiftIndex = findIndex(state.timePairs, { id: payload.shiftTimePairId });

    if (payload.newShiftTimePair.deletedDate) {
      state.timePairs.splice(shiftIndex, 1);
    } else {
      state.timePairs.splice(
        shiftIndex,
        1,
        WorkScheduleUtil.getTimePairWithWorkScheduleData(state, payload.newShiftTimePair),
      );
    }
  },

  setLocales: (state, locales) => {
    state.locales = locales;
  },

  // Manager Approvals
  setManagerApprovals: (state, approvalEmployeeData) => {
    const approvalsByUsername = {};
    forEach(approvalEmployeeData, (employeeData) => {
      for (let i = 0; i < employeeData.summaries.length; i += 1) {
        const timePair = employeeData.summaries[i];
        if (timePair.startingWorkScheduleDay !== null) {
          employeeData.summaries[i] = WorkScheduleUtil.getTimePairWithWorkScheduleData(state, timePair);
        }
        timePair.type = getTimePairClockingType.methods.getTimePairClockingType(timePair);
        Vue.set(state.timePairSelectability, timePair.id, {
          selected: false,
          currentlySelectable: !isNull(timePair.clockOut) && i < constants.Approvals.TIME_PAIRS_PER_PAGE
            && timePair.status !== constants.Approvals.Status.APPROVED
            && timePair.status !== constants.Approvals.Status.REJECTED,
        });
      }
      Vue.set(state.allCurrentEmployeePairsSelected, employeeData.id, false);
      approvalsByUsername[employeeData.id] = employeeData;
    });
    state.approvalsByUsername = approvalsByUsername;
  },

  selectTimePair: (state, payload) => {
    state.timePairSelectability[payload.timePairId].selected = payload.value;
  },

  selectCurrentlySelectablePairsByUsername: (state, payload) => {
    forEach(payload.approvals, (timePair) => {
      if (state.timePairSelectability[timePair.id].currentlySelectable) {
        const timePairSelectability = state.timePairSelectability[timePair.id];
        Vue.set(timePairSelectability, 'selected', payload.value);
        Vue.set(state.timePairSelectability, timePair.id, timePairSelectability);
      }
    });
  },

  updateCurrentTimePairIds: (state, payload) => {
    forEach(payload.approvals, (summary) => {
      Vue.set(
        state.timePairSelectability[summary.id],
        'currentlySelectable',
        (payload.currentItemIds.has(summary.id)
          || (summary.adjustedVersion && payload.currentItemIds.has(summary.adjustedVersion.id)))
        && !isNull(summary.clockOut) && summary.status !== constants.Approvals.Status.APPROVED
        && summary.status !== constants.Approvals.Status.REJECTED,
      );
    });
  },

  setAllCurrentTimePairsSelected: (state, payload) => {
    state.allCurrentTimePairsSelected = payload;
  },

  clearSelectedTimePairs: (state) => {
    forEach(keys(state.approvalsByUsername), (username) => {
      const approvals = state.approvalsByUsername[username];
      forEach(approvals.summaries, (timePair) => {
        const timePairSelectability = state.timePairSelectability[timePair.id];
        Vue.set(timePairSelectability, 'selected', false);
        Vue.set(state.timePairSelectability, timePair.id, timePairSelectability);
      });
    });
  },

  updateGlobalCheckboxes: (state, payload) => {
    const selectableApprovals = filter(
      payload.approvals,
      (timePairSummary) => state.timePairSelectability[timePairSummary.id].currentlySelectable,
    );
    const approvalsSelectedStates = map(
      selectableApprovals,
      (timePairSummary) => state.timePairSelectability[timePairSummary.id].selected,
    );
    const allApprovalsSelected = !isEmpty(approvalsSelectedStates) && every(approvalsSelectedStates);

    Vue.set(state.allCurrentEmployeePairsSelected, payload.username, allApprovalsSelected);

    state.allCurrentTimePairsSelected = every(
      filter(
        map(
          keys(state.approvalsByUsername),
          (username) => (filter(
            state.approvalsByUsername[username].summaries,
            (timePairSummary) => state.timePairSelectability[timePairSummary.id].currentlySelectable,
          ).length === 0 ? null : state.allCurrentEmployeePairsSelected[username]),
        ),
        (value) => value != null,
      ),
    );
  },

  filterApprovalsByDate: (state, dates) => {
    forEach(keys(state.approvalsByUsername), (username) => {
      const approval = state.approvalsByUsername[username];
      const currentTimePairs = approval.summaries;
      const ids = [];

      forEach(currentTimePairs, (timePair) => {
        const startDate = new Date(timePair.startDate.substring(0, timePair.startDate.indexOf('T')));
        if ((dates.length === 1 && (startDate < new Date(dates[0]) || startDate > new Date(dates[0])))
          || startDate < new Date(dates[0]) || startDate > new Date(dates[1])
        ) {
          ids.push(timePair.id);
        }
      });

      forEach(ids, (id) => {
        currentTimePairs.splice(currentTimePairs.findIndex((timePair) => timePair.id === id), 1);
      });

      if (currentTimePairs.length === 0) {
        Vue.delete(state.approvalsByUsername, username);
      }
    });
  },

  updateApprovals: (state, payload) => {
    const targetSummaries = includes(keys(state.approvalsByUsername), payload.employee.id)
      ? state.approvalsByUsername[payload.employee.id].summaries
      : [];

    payload.newTimePair.type = getTimePairClockingType.methods.getTimePairClockingType(payload.newTimePair);
    let currentlySelectable = true;

    const newTimePair = payload.newTimePair.startingWorkScheduleDay == null
      ? payload.newTimePair
      : WorkScheduleUtil.getTimePairWithWorkScheduleData(state, payload.newTimePair);

    if (payload.timePairId === -1) {
      if (targetSummaries.length === 0) {
        Vue.set(
          state.approvalsByUsername,
          payload.employee.id,
          { ...payload.employee, summaries: [newTimePair] },
        );
      } else {
        const firstSelectableIndex = findIndex(
          targetSummaries,
          (timePairSummary) => state.timePairSelectability[timePairSummary.id].currentlySelectable,
        );
        const lastSelectableIndex = findLastIndex(
          targetSummaries,
          (timePairSummary) => state.timePairSelectability[timePairSummary.id].currentlySelectable,
        );

        targetSummaries.push(newTimePair);
        targetSummaries.sort(
          (timePairA, timePairB) => DateTimeUtil.dateTimeIsGreater(timePairB.clockIn.date, timePairA.clockIn.date),
        );

        const currentIndex = findIndex(targetSummaries, (summary) => summary.id === newTimePair.id);
        currentlySelectable = currentIndex >= firstSelectableIndex && currentIndex <= lastSelectableIndex;
      }
    } else {
      let summaryTimePairIndex = findIndex(targetSummaries, { id: payload.timePairId });

      if (summaryTimePairIndex === -1) {
        summaryTimePairIndex = targetSummaries
        .findIndex((timePair) => timePair.adjustedVersion.id === payload.timePairId);
      }
      targetSummaries.splice(summaryTimePairIndex, 1, newTimePair);
    }

    Vue.set(state.timePairSelectability, newTimePair.id, {
      selected: false,
      currentlySelectable,
    });
  },

  removeApprovals: (state, approvalRequest) => {
    if (state.featureSwitches[constants.FEATURE_SWITCHES_TITLES.FILTER_APPROVALS_BY_STATUS]) {
      const removableTimePairIds = flatMap(approvalRequest.statusRequests, (statusRequest) => statusRequest.timePairId);

      forEach(keys(state.approvalsByUsername), (username) => {
        const approval = state.approvalsByUsername[username];
        const currentTimePairs = approval.summaries;
        const indices = [];

        forEach(removableTimePairIds, (timePairId) => {
          const removableIndex = findIndex(currentTimePairs, { id: timePairId });
          if (removableIndex !== -1) {
            currentTimePairs.splice(removableIndex, 1);
            indices.push(timePairId);
          }
        });

        forEach(indices, (timePairId) => {
          removableTimePairIds.splice(removableTimePairIds.indexOf(timePairId), 1);
        });

        if (currentTimePairs.length === 0) {
          Vue.delete(state.approvalsByUsername, username);
        }

        return removableTimePairIds.length !== 0;
      });
    }
  },

  addFilter: (state, addedFilter) => {
    if (!some(map(state.activeFilters, (activeFilter) => isEqual(activeFilter, addedFilter)))) {
      state.activeFilters.push(addedFilter);
    }
  },

  removeFilter: (state, removedFilter) => {
    state.activeFilters = differenceWith(state.activeFilters, [removedFilter], isEqual);
  },

  setSubordinateEmployees: (state, subordinateEmployees) => {
    const subordinates = [];
    const geofences = [];

    forEach(subordinateEmployees, (subordinateEmployee) => {
      const validAssignments = [];

      forEach(subordinateEmployee.subordinateAssignmentDetails, (assignmentDetail) => {
        if (assignmentDetail.employeeAssignment.authorityNames
        .indexOf(AuthorisationUtil.authorities.CAPTURE_TIME) >= 0) {
          validAssignments.push({
            id: assignmentDetail.employeeAssignment.id,
            description: `${assignmentDetail.employeeAssignment.position.description
            } (${assignmentDetail.employeeAssignment.position.id})`,
            timePairCustomFields: assignmentDetail.employeeAssignment.timePairCustomFields,
            timeZoneOffset: assignmentDetail.employeeAssignment.timeZoneOffset,
            currentWorkScheduleCode: assignmentDetail.currentWorkScheduleCode,
            timeRecordingVariant: assignmentDetail.employeeAssignment.timeRecordingVariant,
            assignedTimeRecordingVariants: new Set(assignmentDetail.employeeAssignment.assignedTimeRecordingVariants),
            currentlyManaged: includes(
              assignmentDetail.managerAssignmentAuthorities,
              AuthorisationUtil.authorities.ADD_TIME,
            ),
            tempWorkScheduleAssignmentRequests: assignmentDetail.tempWorkScheduleAssignmentRequests,
            timeTypeProfile: assignmentDetail.employeeAssignment.timeTypeProfile,
          });

          if (hasLocationFeature.computed.isGeofenceFeatureActive) {
            geofences.push({
              assignmentId: assignmentDetail.employeeAssignment.id,
              geofences: assignmentDetail.employeeAssignment.geofences,
            });
          }
        }
      });

      subordinates.push({
        employee: subordinateEmployee.employee,
        subordinateAssignments: validAssignments,
        activeTimePair: subordinateEmployee.activeTimePair,
      });
    });

    subordinates.sort();

    state.subordinateEmployees = subordinates;
    state.geofence = state.geofence.concat(geofences);

    state.workSchedules = merge(
      state.workSchedules,
      keyBy((map(
        filter(
          flatMap(
            map(subordinateEmployees, (subordinateEmployee) => subordinateEmployee.subordinateAssignmentDetails),
          ),
          (assignment) => assignment.workScheduleSummary != null,
        ),
        (assignment) => assignment.workScheduleSummary,
      )), 'assignmentId'),
    );
  },

  updateSubordinateEmployeeActiveTimePair: (state, clockingPayload) => {
    const index = findIndex(state.subordinateEmployees, { employee: { id: clockingPayload.employeeId } });
    const subordinate = state.subordinateEmployees[index];

    subordinate.activeTimePair = clockingPayload.timePair.clockOut ? null : clockingPayload.timePair;
    state.subordinateEmployees.splice(index, 1, subordinate);
  },

  resetSubordinateEmployeeActiveTimePair: (state, employeeInformation) => {
    const index = findIndex(state.subordinateEmployees, { employee: { id: employeeInformation.employeeId } });
    const subordinate = state.subordinateEmployees[index];

    subordinate.activeTimePair = null;
    state.subordinateEmployees.splice(index, 1, subordinate);
  },

  setWorkSchedules: (state, workSchedulePayload) => {
    state.workSchedulesByExternalCode = keyBy(
      map(workSchedulePayload, (workSchedule, index) => assign(
        {},
        workSchedule,
        { colour: WorkScheduleUtil.getWorkScheduleColour(index) },
      )),
      (workSchedule) => workSchedule.externalCode,
    );
  },

  setDailyWorkScheduleByEmployee: (state, dailyWorkScheduleByEmployeePayload) => {
    state.dailyWorkScheduleByEmployee = dailyWorkScheduleByEmployeePayload;
    Object.keys(state.dailyWorkScheduleByEmployee).forEach((employee) => {
      state.dailyWorkScheduleByEmployee[employee].forEach((day) => {
        if (!day.startTime) {
          const dayWithStartTime = state.dailyWorkScheduleByEmployee[employee].find((it) => it.startTime);
          day.startTime = dayWithStartTime ? dayWithStartTime.startTime : null;
        }

        if (!day.endTime) {
          const dayWithEndTime = state.dailyWorkScheduleByEmployee[employee].find((it) => it.endTime);
          day.endTime = dayWithEndTime ? dayWithEndTime.endTime : null;
        }
      });
    });
  },

  setPendingWorkSchedules: (state, workSchedules) => {
    state.pendingWorkSchedules = workSchedules;
  },

  setAssignedWorkScheduleRanges: (state, assignedWorkScheduleRangesPayload) => {
    forEach(assignedWorkScheduleRangesPayload,
      (assignmentRanges) => {
        forEach(assignmentRanges.assignedWorkScheduleRangeSummaries, (assignmentRange) => {
          assignmentRange.startDate = new Date(assignmentRange.startDate);
          assignmentRange.endDate = new Date(assignmentRange.endDate);
        });
      });
    Vue.set(state, 'assignedWorkScheduleRanges', reduce(
      assignedWorkScheduleRangesPayload,
      (accumulator, { assignmentId, assignedWorkScheduleRangeSummaries }) => ({
        ...accumulator, [assignmentId]: assignedWorkScheduleRangeSummaries,
      }),
      {},
    ));
  },

  setEmployeeSchedules: (state, assignedWorkScheduleRangesPayload) => {
    const assignments = [];
    forEach(assignedWorkScheduleRangesPayload,
      (assignmentRanges) => {
        const summaries = [];
        forEach(assignmentRanges.assignedWorkScheduleRangeSummaries, (assignmentRange) => {
          summaries.push({
            startDate: new Date(assignmentRange.startDate),
            endDate: new Date(assignmentRange.endDate),
            minuteRange: assignmentRange.minuteRange,
            externalCode: assignmentRange.externalCode,
            startingWorkScheduleDay: assignmentRange.workScheduleDayNumberFromDate,
            type: assignmentRange.type,
            isTemporary: assignmentRange.isTemporary,
            isOffDay: assignmentRange.isOffDay,
            assignedWorkSchedule: assignmentRange.assignedWorkSchedule,
          });
        });

        assignments.push({
          assignmentId: assignmentRanges.assignmentId,
          currentPosition: assignmentRanges.currentPosition,
          assignedWorkScheduleRangeSummaries: summaries,
        });
      });

    state.employeeSchedules = assignments;
  },

  setTempWorkScheduleAssignmentStatusPolling: (state, value) => {
    state.tempWorkScheduleAssignmentStatusPolling = value;
  },

  updateTempWorkScheduleAssignmentRequestStatuses: (state, requestSummariesPayload) => {
    const subordinateAssignmentsById = keyBy(
      flatMap(map(state.subordinateEmployees, (employee) => employee.subordinateAssignments)),
      'id',
    );

    let workSchedulesToQuery = [];

    forEach(requestSummariesPayload, (updatedRequest) => {
      const assignmentRequests = subordinateAssignmentsById[updatedRequest.assignmentId]
      .tempWorkScheduleAssignmentRequests;

      const requestFailed = updatedRequest.ecSynchronisationStatus
        === constants.EC_SYNCHRONISATION_STATUS.SYNCHRONISATION_FAILED;

      if (requestFailed) {
        Vue.set(
          subordinateAssignmentsById[updatedRequest.assignmentId],
          'tempWorkScheduleUpsertFailed',
          true,
        );
        const index = state.pendingWorkSchedules.indexOf(updatedRequest.tempWorkScheduleExternalCode);
        if (index > -1) {
          state.pendingWorkSchedules.splice(index, 1);
        }
      } else {
        workSchedulesToQuery = workSchedulesToQuery.concat([updatedRequest.tempWorkScheduleExternalCode]);
      }

      const currentRequestSummaryIndex = findIndex(
        assignmentRequests,
        (request) => request.tempWorkScheduleExternalCode === updatedRequest.tempWorkScheduleExternalCode,
      );

      if (currentRequestSummaryIndex !== -1) {
        assignmentRequests.splice(currentRequestSummaryIndex, 1, updatedRequest);
      } else {
        assignmentRequests.push(updatedRequest);
      }
    });

    state.pendingWorkSchedules = workSchedulesToQuery;
  },

  // Clock
  resetActiveTimePair: (state) => {
    state.activeTimePair = {
      id: null,
      employeeAssignment: null,
      clockIn: null,
      clockOut: null,
      startDate: null,
      isCorrection: null,
      isManualPair: null,
      timePairCustomFieldValues: [],
    };
  },

  setActiveTimePair: (state, timePair) => {
    state.activeTimePair = timePair;
  },

  setTimerDuration: (state, elapsedTime) => {
    state.timer.duration = elapsedTime;
  },

  setTimerTrueClockIn: (state, clockIn) => {
    state.timer.trueClockInTime = clockIn ? clockIn.date : new Date();
    state.timer.timeZoneOffset = clockIn ? clockIn.timeZoneOffset : new Date().getTimezoneOffset();
  },

  setUserConfiguration: (state, userConfiguration) => {
    state.userConfiguration = userConfiguration;
    DateTimeUtil.switchTimeFormat(state.userConfiguration.is24HourDateTime);
  },

  setActiveSegmentType: (state, pairType) => {
    state.activeSegmentType = pairType;
    state.activeSegment = null;
  },

  // Snackbar
  setSnackbar: (state, snackbar) => {
    state.snackbar = { ...snackbar };
  },

  setSnackbarVisible: (state, isVisible) => {
    state.snackbar = { ...state.snackbar, visible: isVisible };
  },

  setAppDialog: (state, dialog) => {
    state.appDialog = { ...dialog };
  },

  setAppDialogVisible: (state, visibility) => {
    state.appDialog = { ...state.appDialog, ...visibility };
  },

  setAppLoader: (state, loader) => {
    if (!loader.text) loader.text = i18n.t('appLoader.loading');

    state.appLoader = { ...loader };
  },

  setNoLocationDataRequired: (state, required) => {
    localStorage.setItem(constants.LocalStorage.NO_LOCATION_DATA, required);
  },

  setGoogleMapsApi(state, api) {
    state.googleMapsApi = api;
  },

  setFeatureSwitches(state, featureInfo) {
    state.featureSwitches = featureInfo.features;
    state.featureSchedules = featureInfo.schedules;
  },

  setTheme(state, themeInfo) {
    if (themeInfo) {
      state.theme = themeInfo;
    }
  },

  setEmployee: (state, assignments) => {
    state.employee.assignments = assignments;

    if (hasLocationFeature.computed.isGeofenceFeatureActive) {
      const geofences = [];

      forEach(assignments, (assignment) => {
        geofences.push({
          assignmentId: assignment.id,
          geofences: assignment.geofences,
        });
      });
      state.geofence = geofences;
    }
  },

  setApprovalStatusConfiguration(state, statuses) {
    if (constants.FEATURE_SWITCHES_TITLES.FILTER_APPROVALS_BY_STATUS && statuses !== null && statuses.length !== 0) {
      state.approvalStatusConfiguration = statuses;
    } else {
      state.approvalStatusConfiguration = [constants.Approvals.Status.INCOMPLETE,
        constants.Approvals.Status.PENDING_DELETE, constants.Approvals.Status.PENDING];
    }
  },

  setEmployeeAbsences(state, absencesPayload) {
    state.employeeAbsences = absencesPayload;
  },
};

export default mutations;
